import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, signal, inject, ViewEncapsulation, effect, ChangeDetectorRef, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { DrawerModule } from '@progress/kendo-angular-layout';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { PopupModule } from '@progress/kendo-angular-popup';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject, Observable, of } from 'rxjs';
import { SidebarMenuItem } from './sidebar-menu-item.interface';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    DrawerModule,
    ButtonModule,
    PopupModule,
    RouterModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class SidebarComponent implements OnInit, OnChanges, OnDestroy {
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  @Input() menuItems: SidebarMenuItem[] = [];
  @Input() menuItems$?: Observable<SidebarMenuItem[]>;
  @Input() logoSrc?: string;
  @Input() logoAlt?: string;
  @Input() appName?: string;
  @Input() expandedWidth = 272;
  @Input() collapsedWidth = 80;
  @Input() isExpanded: boolean | (() => boolean) = true;
  @Input() autoExpandCollapseByRoute = true;

  @Output() expandedChange = new EventEmitter<boolean>();
  @Output() collapseToggle = new EventEmitter<string>();

  private _isExpanded = signal(true);
  openCollapseItems = new Set<string>();
  currentUrl = '';

  /** Collapsed-mode popup state */
  popupAnchorEl: ElementRef | null = null;
  popupItem: SidebarMenuItem | null = null;

  displayMenuItems$: Observable<SidebarMenuItem[]>;

  constructor() {
    this.displayMenuItems$ = this.menuItems$ || of(this.menuItems);

    effect(() => {
      const isExpandedValue = typeof this.isExpanded === 'function'
        ? this.isExpanded()
        : this.isExpanded;

      this._isExpanded.set(isExpandedValue);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isExpanded) {
      const newValue = typeof this.isExpanded === 'function'
        ? this.isExpanded()
        : this.isExpanded;

      this._isExpanded.set(newValue);
    }

    if (changes.menuItems || changes.menuItems$) {
      this.displayMenuItems$ = this.menuItems$ || of(this.menuItems);
    }
  }

  ngOnInit(): void {
    const initialExpanded = typeof this.isExpanded === 'function'
      ? this.isExpanded()
      : this.isExpanded;

    this._isExpanded.set(initialExpanded);

    this.currentUrl = this.router.url;

    if (this.autoExpandCollapseByRoute) {
      this.updateCollapseState(this.router.url);
    }

    // Listen to route changes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.url;
        this.closePopup();
        this.cdr.markForCheck();

        if (this.autoExpandCollapseByRoute) {
          this.updateCollapseState(event.url);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateCollapseState(url: string): void {
    if (url.startsWith('/admin')) {
      this.openCollapseItems.add('admin');
    } else {
      this.openCollapseItems.delete('admin');
    }
  }

  onToggleSidebar(): void {
    this.closePopup();
    const newValue = !this._isExpanded();

    this._isExpanded.set(newValue);
    this.expandedChange.emit(newValue);
  }

  onCollapseToggle(itemId: string): void {
    if (this.openCollapseItems.has(itemId)) {
      this.openCollapseItems.delete(itemId);
    } else {
      this.openCollapseItems.add(itemId);
    }
    this.collapseToggle.emit(itemId);
  }

  isCollapseOpen(itemId: string): boolean {
    return this.openCollapseItems.has(itemId);
  }

  get isExpandedValue(): boolean {
    return this._isExpanded();
  }

  isItemActive(item: SidebarMenuItem): boolean {
    if (item.type === 'collapse' || !item.path) {
      return false;
    }

    return this.currentUrl.startsWith(item.path);
  }

  isChildActive(child: SidebarMenuItem): boolean {
    return child.path ? this.currentUrl.startsWith(child.path) : false;
  }

  onItemClick(item: SidebarMenuItem): void {
    if (item.type === 'collapse') {
      this.onCollapseToggle(item.id);
    } else if (item.path) {
      this.router.navigate([item.path]);
    }
  }

  onChildItemClick(child: SidebarMenuItem): void {
    if (child.path) {
      this.closePopup();
      this.router.navigate([child.path]);
    }
  }

  /** Opens the subitems popup when a collapse item is clicked in collapsed mode */
  openSubitemsPopup(item: SidebarMenuItem, event: MouseEvent): void {
    const target = (event.currentTarget || event.target) as HTMLElement;

    // Toggle off if same item is already open
    if (this.popupItem?.id === item.id) {
      this.closePopup();

      return;
    }

    this.popupAnchorEl = new ElementRef(target);
    this.popupItem = item;
  }

  closePopup(): void {
    this.popupAnchorEl = null;
    this.popupItem = null;
  }

  /** Close the popup when user clicks outside of it */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.popupItem) {
      return;
    }

    const target = event.target as HTMLElement;

    // Check if click is inside the popup or its anchor
    const popup = document.querySelector('.sidebar-subitems-popup');
    const anchor = this.popupAnchorEl?.nativeElement as HTMLElement;

    if (popup?.contains(target) || anchor?.contains(target)) {
      return;
    }

    this.closePopup();
  }
}

